/**
 * AnalyticsProvider
 * 
 * React context provider that automatically tracks page views on route changes.
 * Wrap your app with this to enable analytics tracking.
 */

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics';

interface AnalyticsContextValue {
  trackCTAClick: (buttonText: string, pageLocation: string, buttonType?: string) => void;
  trackTreatmentView: (treatmentName: string, category?: string) => void;
  trackDoctorView: (doctorName: string, specialty?: string) => void;
  trackHospitalView: (hospitalName: string, city?: string) => void;
  trackFormStart: (formType: string, page: string) => void;
  trackFormSubmit: (formType: string, success: boolean, treatment?: string) => void;
  trackPhoneClick: (page: string) => void;
  trackWhatsAppClick: (page: string) => void;
  trackSearch: (searchTerm: string) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

/**
 * Hook to access analytics tracking functions from context
 */
export function useAnalyticsContext() {
  const context = useContext(AnalyticsContext);
  if (!context) {
    // Return no-op functions if provider is not found (graceful degradation)
    return {
      trackCTAClick: () => {},
      trackTreatmentView: () => {},
      trackDoctorView: () => {},
      trackHospitalView: () => {},
      trackFormStart: () => {},
      trackFormSubmit: () => {},
      trackPhoneClick: () => {},
      trackWhatsAppClick: () => {},
      trackSearch: () => {},
    };
  }
  return context;
}

interface AnalyticsProviderProps {
  children: ReactNode;
}

/**
 * Analytics Provider Component
 * - Automatically tracks page views on route changes
 * - Provides tracking functions to child components
 */
export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const location = useLocation();
  const analytics = useAnalytics();

  // Track page views on route change
  useEffect(() => {
    // Small delay to ensure page title is updated
    const timer = setTimeout(() => {
      analytics.trackPageView(location.pathname, document.title);
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname, analytics.trackPageView]);

  const value: AnalyticsContextValue = {
    trackCTAClick: analytics.trackCTAClick,
    trackTreatmentView: analytics.trackTreatmentView,
    trackDoctorView: analytics.trackDoctorView,
    trackHospitalView: analytics.trackHospitalView,
    trackFormStart: analytics.trackFormStart,
    trackFormSubmit: analytics.trackFormSubmit,
    trackPhoneClick: analytics.trackPhoneClick,
    trackWhatsAppClick: analytics.trackWhatsAppClick,
    trackSearch: analytics.trackSearch,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}
